/**
 * Instructions — Wave 4 native step page.
 *
 * Route: /purchase-flow-revamp/instructions
 *
 * Native rewrite over `instructionsSlice` (Kind A, MODULE-CONTRACT §11). Renders
 * up to 4 native sub-sections — CT booking, meeting scheduler, extra-services
 * picker, free-form comments — inside the shared `StepShell` (cart sidebar +
 * sticky top bar + step nav live there). Replaces the legacy
 * `InstructionStepController` that was mounted through the ~19-provider
 * `LegacyStepShell`.
 *
 * Section visibility is BE-driven (`visibility.helpers`, W-11 / V-06): the CT
 * booking picker needs an advertised `CT_BOOKING` type and the meeting card's
 * site-access half needs ≥1 non-synthetic mode, both on the same
 * loading-hides / error-fails-open ladder DF-I9 established for the modes.
 *
 * Data seam: the instruction catalogue (meeting options / extras / timezone)
 * comes from the native `useInstructionCatalogue` hook (provider-free, Path A) —
 * the v2 PRODUCT catalogue carries no instruction data (`extras` empty under v2).
 *
 * §11.1 — NO mount-time write to any PERSISTED slice field. Effective values are
 * read at RENDER (`slice.field ?? default`); setters fire ONLY on user
 * interaction. `scheduledByCT` is read as `scheduledByCT ?? default` and written
 * only when the user toggles the checkbox — it is in the autosave fingerprint
 * with no other writer, so a mount-seed would dirty every draft (F-B1 class).
 *
 * §11.2 — the ONE sanctioned mount-time write: `extrasFromInstructions` is
 * display-only (absent from serializeDraft + the autosave fingerprint, and the
 * setter is value-guarded), re-derived from persisted `extraServices` × the
 * instruction catalogue so the cart-total extras line survives resume. V-16
 * adds the key-pickup travel fee the selected meeting mode implies to that same
 * mirror — display only; `serializePlacement` never sees it, because the BE
 * derives the fee line from the organization method itself. The favourite-
 * creative travel fee rides the same mirror, for the same reason:
 * the BE derives that line from `selectedFavouriteCreativeId`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, Stack } from '@mui/material'

import { SystemMessage } from 'components/common/SystemMessage'

import { logger } from 'utils/logging'
import type { ProductKind } from 'constants/product'
import type { SalesforceId } from 'models/purchaseFlowRevamp'
import { useAuth0 } from 'utils/auth'
import { useTimezone } from 'components/contexts/timezone.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { buildAvailableCreativesBody, useAvailableCreatives } from 'dataQueries/purchaseFlowRevampCreatives.query'
import { useKeyPickupTravelCost } from 'dataQueries/purchaseFlowRevampKeyPickupTravelCost.query'
import { cartHasMeasurementProduct, cartIsMeasurementOnly, cartProductKinds } from 'dataQueries/purchaseFlowRevampProductCodes'
import type { MeetingModeRestriction } from './meeting.helpers'

import { useSyncStepWithRoute } from '../../routing/useSyncStepWithRoute'
import { useRequireActiveSession } from '../../routing/useRequireActiveSession'
import { useBeforeUnloadGuard } from '../../routing/useBeforeUnloadGuard'
import { PfPageHeading, PfRetryAlert, StepShell } from '../../components'
import { CORAL_600 } from 'constants/styling/theme'
import { usePfStore } from '../../store'
import { selectInstructionsStepErrors, selectInstructionsStepSkipped } from '../../store/selectors'
import { refineInstructionsErrors } from '../../components/cart/refineInstructionsErrors'

import { useInstructionCatalogue } from './useInstructionCatalogue'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { ctBookingChosenPayload, ctBookingOpenedPayload } from './ctBookingAnalytics'
import { resolveContactPrefill, resolvePrefillTarget } from './contactPrefill.helpers'
import { usePfSubscriptionEntitlement } from './usePfSubscriptionEntitlement'
import { resolveMeetingModes } from './meeting.helpers'
import { meetingToRestoreOnFixed } from './meetingChoice.helpers'
import { isMeetingDateTooFar, resolveMeetingMaxDateTime, resolveMeetingMinDateTime } from './leadTime.helpers'
import { resolveCtBookingVisible, resolveMeetingAwaitsSchedule, resolveMeetingSectionVisible, resolveScheduleSectionVisible } from './visibility.helpers'
import { SUBSCRIPTION_ONLY_EXTRA_KINDS, partitionExtrasByWireSlot, keyPickupTravelQuoteRequest, offerableExtras } from './extras.helpers'
import { ShootingScheduleSection, type ScheduleMethod } from './sections/shootingSchedule'
import { composeDateTime, withScheduledDate } from './sections/shootingSchedule/shootingDateTime'
import { useScheduleParts } from './sections/shootingSchedule/useScheduleParts'
import { MeetingSection, ExtrasSection, CommentsSection } from './sections'
import { PfStepPath, usePfPath } from '../../routing/pfBasePath'

/**
 * @page Instructions
 * Native instructions step — 4 sub-sections over `instructionsSlice`.
 *
 * @example
 * // Registered in PurchaseFlowRevamp.page.tsx as:
 * <Route path="instructions" element={<Instructions />} />
 */
export const Instructions: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const navigate = useNavigate()
  const pfPath = usePfPath()

  useSyncStepWithRoute('instructions')
  useRequireActiveSession(pfPath(PfStepPath.CATALOGUE))
  useBeforeUnloadGuard()

  // ── Store reads (effective values read at render — §11.1) ─────────────────────
  const property = usePfStore((s) => s.property)
  const items = usePfStore((s) => s.items)
  const meeting = usePfStore((s) => s.meeting)
  const ctBookingId = usePfStore((s) => s.ctBookingId)
  const targetUserEmail = usePfStore((s) => s.targetUserEmail)
  const targetUserName = usePfStore((s) => s.targetUserName)
  const targetUserPhone = usePfStore((s) => s.targetUserPhone)
  const extraServices = usePfStore((s) => s.extraServices)
  const comments = usePfStore((s) => s.comments)

  // ── Store writes (fired ONLY on user interaction) ─────────────────────────────
  const setMeeting = usePfStore((s) => s.setMeeting)
  const setMeetingPhoneInvalid = usePfStore((s) => s.setMeetingPhoneInvalid)
  const setMeetingDateTooSoon = usePfStore((s) => s.setMeetingDateTooSoon)
  const setMeetingDateTooFar = usePfStore((s) => s.setMeetingDateTooFar)
  const setKeyPickupTravelQuotePending = usePfStore((s) => s.setKeyPickupTravelQuotePending)
  const setCtBookingId = usePfStore((s) => s.setCtBookingId)
  const setExtraServices = usePfStore((s) => s.setExtraServices)
  const setComments = usePfStore((s) => s.setComments)

  // ── Catalogue (skip gate + Back resolution + scheduledByCT default) ───────────
  const { baseUserData } = useUserData()
  const catalogueQuery = usePfCatalogue()
  const catalogue = catalogueQuery.data

  // ── Instruction catalogue (native, provider-free) ─────────────────────────────
  const instructionCatalogue = useInstructionCatalogue()

  // ── "I will be onsite" prefill (legacy targetUser-vs-baseUserData split) ───────
  // Whoever the order is FOR supplies the contact: an admin ordering on behalf
  // fills the target client's own details, everyone else their own. Legacy did
  // exactly this by resolving one `targetUser` object up front
  // (`TargetOrderUser.context.ts`) and prefilling from it.
  const prefillInput = useMemo(
    () => ({ targetUserEmail, targetUserName, targetUserPhone, user: baseUserData }),
    [targetUserEmail, targetUserName, targetUserPhone, baseUserData]
  )
  const prefillTarget = resolvePrefillTarget(prefillInput)
  const contactPrefill = useMemo(() => resolveContactPrefill(prefillInput), [prefillInput])

  // ── DF-I9 — catalogue-driven meeting modes ────────────────────────────────────
  // Offer only the organization meeting kinds the BE actually returned (+ synthetic
  // NONE, + any resumed-draft mode), rather than a hardcoded 4. While the catalogue
  // resolves the section shows a spinner instead of a possibly-wrong subset.
  //
  // The one thing the response cannot answer: with the On-Site Area Report in the
  // cart only a third-party contact is valid, because a technician books the
  // appointment afterwards. The BE keeps offering all three (it only swaps in the
  // free third-party variant), so the narrowing is ours — as it was in legacy.
  const isModesLoading = instructionCatalogue.status === 'loading' || instructionCatalogue.status === 'idle'

  const restrictToThirdParty = useMemo(
    () => cartHasMeasurementProduct(items, catalogue),
    [items, catalogue]
  )

  // A measurement-only cart has nothing shot, so legacy hid the favourite-creative
  // picker, the extra services and the briefing comments and left only the
  // organization section (`isOnlyMeasurementProduct`). The BE does not narrow
  // these either — it advertises CT_BOOKING for the whole REAL_ESTATE category —
  // so the gate is ours, and it is the STRICTER predicate: a mixed cart still has
  // a shoot to brief and a creative to request.
  const isMeasurementOnly = useMemo(
    () => cartIsMeasurementOnly(items, catalogue),
    [items, catalogue]
  )
  // ── DF-I3 — the lead-time floor for the meeting date ──────────────────────────
  // v2 shipped `disablePast` only, so a shoot could be booked minutes out.
  // `resolveMeetingMinDateTime` ports legacy's ladder (no timezone / admin /
  // prestige / favourite-creative / 48h-with-weekend-roll-forward).
  const { roles } = useAuth0()
  // The client's own clock, for DF-I3's mismatch warning + dual echo. The date
  // field itself is entered in the SHOOT's timezone.
  const { userTimezone } = useTimezone()
  const cartKinds = useMemo(() => cartProductKinds(items, catalogue), [items, catalogue])
  const minDateTime = useMemo(
    () => resolveMeetingMinDateTime({
      timezone: instructionCatalogue.timezone,
      isAdmin: roles.isAdmin,
      cartKinds,
      hasSelectedCreative: !!ctBookingId,
    }),
    [instructionCatalogue.timezone, roles.isAdmin, cartKinds, ctBookingId]
  )

  // The gate cannot compute the floor itself (it depends on catalogue data), so
  // report the verdict the way the phone's is reported. Re-derived on every
  // change, so it self-corrects when the mode, the date or the cart moves.
  const chosenDate = meeting?.onSite?.date ?? meeting?.keyPickup?.date
  useEffect(() => {
    const tooSoon = !!chosenDate && !!minDateTime && moment(chosenDate).isBefore(moment(minDateTime))
    setMeetingDateTooSoon(tooSoon)
  }, [chosenDate, minDateTime, setMeetingDateTooSoon])

  useEffect(() => {
    const maxDateTime = resolveMeetingMaxDateTime(instructionCatalogue.timezone)
    setMeetingDateTooFar(!!chosenDate && isMeetingDateTooFar(chosenDate, maxDateTime, instructionCatalogue.timezone))
  }, [chosenDate, instructionCatalogue.timezone, setMeetingDateTooFar])

  // Fixed or flexible — the prototype's one question, asked above the meeting
  // cards because it decides which of them are even valid.
  // Seeded from the draft so a resumed order reopens on the answer it was saved
  // with: a third party or a creative-arranged pickup is flexible, a dated
  // meeting is fixed.
  // The stored choice wins: flexible with no meeting answered yet leaves nothing to derive it from.
  const storedScheduleMethod = usePfStore((s) => s.scheduleMethod)
  const storeScheduleMethod = usePfStore((s) => s.setScheduleMethod)
  const [scheduleMethod, setLocalScheduleMethod] = useState<ScheduleMethod | undefined>(() => {
    if (storedScheduleMethod) return storedScheduleMethod
    if (meeting?.mode === 'THIRD_PARTY' || meeting?.keyPickup?.arrangedByCreative) return 'flexible'
    if (meeting?.onSite?.date || meeting?.keyPickup?.date) return 'fixed'
    return undefined
  })
  // A draft restored after this page mounted still reopens on its choice.
  useEffect(() => {
    if (!scheduleMethod && storedScheduleMethod) setLocalScheduleMethod(storedScheduleMethod)
  }, [scheduleMethod, storedScheduleMethod])
  const setScheduleMethod = useCallback((next: ScheduleMethod) => {
    setLocalScheduleMethod(next)
    storeScheduleMethod(next)
  }, [storeScheduleMethod])

  // Switching to flexible drops the fixed meeting (its mode is no longer offered); keep it aside so
  // switching back restores what the client entered, unless they have set up a fixed meeting since.
  const parkedFixedMeeting = useRef<typeof meeting>(undefined)
  const pickScheduleMethod = useCallback((next: ScheduleMethod) => {
    if (next === scheduleMethod) return
    if (next === 'flexible' && meeting) parkedFixedMeeting.current = meeting
    const restored = next === 'fixed' ? meetingToRestoreOnFixed(meeting, parkedFixedMeeting.current) : undefined
    if (restored) setMeeting(restored)
    setScheduleMethod(next)
  }, [scheduleMethod, meeting, setMeeting, setScheduleMethod])

  // Two independent reasons the offered modes are narrower than the catalogue's,
  // and the CART's reason wins: with a measurement product the appointment is
  // booked by a technician afterwards, so there is no time for the client to set
  // whatever they picked above. That is also why the scheduling section is hidden
  // in that case — see `showScheduleSection` below.
  const meetingModeRestriction: MeetingModeRestriction = restrictToThirdParty
    ? 'third-party-only'
    : scheduleMethod === 'flexible'
      ? 'flexible'
      : scheduleMethod === 'fixed'
        ? 'no-third-party'
        : 'none'

  const availableModes = useMemo(
    () => resolveMeetingModes(instructionCatalogue.organizationInstructions, instructionCatalogue.status, meeting?.mode, meetingModeRestriction),
    [instructionCatalogue.organizationInstructions, instructionCatalogue.status, meeting?.mode, meetingModeRestriction]
  )

  // A resumed draft can hold a mode the cart no longer permits (measurement added
  // to a cart that already had an on-site meeting). The section stops SHOWING it,
  // but the store would keep it and the Next gate would validate the wrong branch,
  // so drop it here. Not a §11.1 default-seed: it clears state that has become
  // invalid, and only ever fires when it is.
  // When the measurement product is what removed it, the client is told why (QA-506): the meeting they
  // entered on an earlier visit would otherwise just be gone.
  const [measurementDroppedMeeting, setMeasurementDroppedMeeting] = useState(false)
  useEffect(() => {
    if (!meeting?.mode) return
    if (availableModes.includes(meeting.mode)) return
    if (isModesLoading) return
    if (restrictToThirdParty) setMeasurementDroppedMeeting(true)
    setMeeting(undefined)
  }, [meeting?.mode, availableModes, isModesLoading, restrictToThirdParty, setMeeting])

  // ── W-11 — catalogue-driven CT-booking section ────────────────────────────────
  // Same ladder as the meeting modes: render the favourite-creative picker only
  // when the response advertises a CT_BOOKING instruction type (or a resumed
  // draft already holds a selection). Loading hides it rather than flashing it
  // in; an error shows it, so a failed options call never blocks the offer.
  // Choosing the creative is open to every plan; the BE offers CT_BOOKING for the whole REAL_ESTATE category.
  const isSubscribed = usePfSubscriptionEntitlement()

  const showCtBooking = resolveCtBookingVisible(
    instructionCatalogue.ctBookingOptions && instructionCatalogue.ctBookingOptions.length > 0,
    instructionCatalogue.status,
    ctBookingId !== undefined
  )

  // The listed creatives, for the §11.2 travel-fee mirror below. Same hook the
  // picker mounts and every input is resolved inside it, so this shares ONE
  // React Query cache entry with the section — no second request. Unfiltered by
  // name on purpose: the mirror must resolve the SELECTED creative even while
  // the picker's search box narrows what it displays.
  // Loaded only once the client switches the choice on: the backend computes a driving distance per creative.
  const { creatives: listedCreatives, status: creativesStatus, refetch: refetchCreatives } = useAvailableCreatives({ enabled: ctBookingId !== undefined })

  // ── Shooting scheduling ──────────────────────────────────────────────────────
  // The prototype's one container for "when, and by whom". It now owns the date
  // the order is placed with: the meeting cards below no longer ask for one.
  //
  // The three values are held here rather than derived from the draft because
  // the client can pick a day before a meeting mode exists to hang it on. They
  // are SEEDED from the draft (so a resumed order shows its own booking) and
  // written back the moment a dated mode is available — see the effect below.
  const schedule = useScheduleParts(chosenDate, instructionCatalogue.timezone)
  const { day: scheduleDay, hour: scheduleHour, minute: scheduleMinute } = schedule.parts

  // The cart has already answered "when": a technician books the measurement
  // afterwards. Force the answer rather than let the client pick a time that
  // `withScheduledDate` would then drop on the way to the wire.
  useEffect(() => {
    if (!restrictToThirdParty) return
    if (scheduleMethod === 'flexible') return
    setScheduleMethod('flexible')
  }, [restrictToThirdParty, scheduleMethod, setScheduleMethod])

  // Nor can the client pick the creative then: drop a pick made before the report was added.
  useEffect(() => {
    if (!restrictToThirdParty || ctBookingId === undefined) return
    if (ctBookingId) {
      logger.info('PF revamp: creative pick cleared, the on-site area report is booked by a technician', undefined, {
        context: { component: 'Instructions', action: 'clear_creative_for_measurement', metadata: { ctBookingId } },
      })
    }
    setCtBookingId(undefined)
  }, [restrictToThirdParty, ctBookingId, setCtBookingId])

  // A day and an hour without minutes is no time yet, and nothing on screen says so: the gate names it (QA-647).
  // No minute is preselected, so the client sets the time they mean.
  const setMeetingMinutesMissing = usePfStore((s) => s.setMeetingMinutesMissing)
  const minutesMissing = scheduleMethod === 'fixed' && !!scheduleDay && scheduleHour !== undefined && scheduleMinute === undefined
  useEffect(() => {
    setMeetingMinutesMissing(minutesMissing)
  }, [minutesMissing, setMeetingMinutesMissing])

  const scheduledAt = useMemo(
    () => composeDateTime(
      { day: scheduleDay, hour: scheduleHour, minute: scheduleMinute },
      instructionCatalogue.timezone
    ),
    [scheduleDay, scheduleHour, scheduleMinute, instructionCatalogue.timezone]
  )

  // Writing the pick onto the meeting is what puts it on the wire
  // (`serializePlacement` reads `meeting.onSite.date` / `meeting.keyPickup.date`).
  // `withScheduledDate` returns the SAME object when there is nothing to change,
  // which is what keeps this effect from writing on every render.
  useEffect(() => {
    const next = withScheduledDate(meeting, scheduledAt, scheduleMethod)
    if (next === meeting) return
    setMeeting(next)
  }, [meeting, scheduledAt, scheduleMethod, setMeeting])

  const choosingCreative = ctBookingId !== undefined
  // Only creatives the client worked with or saved as favourites are offered. Once the list came back empty the
  // switch stays off and explains it (QA-646), so the costly list is not asked for again for this cart. The list
  // depends on the cart, so an edit from the sidebar offers the switch again.
  const creativesBodyKey = useMemo(
    () => JSON.stringify(buildAvailableCreativesBody(items, catalogueQuery.data)),
    [items, catalogueQuery.data]
  )
  const [emptyCreativesBodyKey, setEmptyCreativesBodyKey] = useState<string | null>(null)
  const noCreativesAvailable = emptyCreativesBodyKey === creativesBodyKey
  const toggleChoosingCreative = (next: boolean) => {
    setCtBookingId(next ? '' : undefined)
  }

  // ── V-06 — hide the meeting card when the BE offers no site access ────────────
  // Editing / staging / documents carts come back with an EMPTY ORGANIZATION
  // list, which left a "Tell us how the creative gets on site" card asking about
  // a visit the cart does not need.
  const showMeeting = resolveMeetingSectionVisible(availableModes, instructionCatalogue.status)
  // With the on-site area report a technician books the appointment, so there is nothing to schedule here.
  const showScheduleSection = resolveScheduleSectionVisible(showMeeting, showCtBooking, restrictToThirdParty)

  // A sole offered mode is shown as chosen but only stored on the first keystroke; the gate's hint then asks
  // for the contact details rather than for an access choice the client sees as made.
  const setMeetingModeImplied = usePfStore((s) => s.setMeetingModeImplied)
  const meetingModeImplied = showMeeting && availableModes.length === 1 && !meeting?.mode
  useEffect(() => {
    setMeetingModeImplied(meetingModeImplied)
  }, [meetingModeImplied, setMeetingModeImplied])
  const meetingAwaitsSchedule = resolveMeetingAwaitsSchedule(showScheduleSection, scheduleMethod)

  const optionsFailed = instructionCatalogue.status === 'error'


  const trackAnalytics = usePfRevampAnalytics()
  const pickScheduleMethodTracked = useCallback((next: ScheduleMethod) => {
    if (next !== scheduleMethod) trackAnalytics({ type: PfRevampEventName.SCHEDULE_METHOD_SELECTED, payload: { method: next } })
    pickScheduleMethod(next)
  }, [scheduleMethod, trackAnalytics, pickScheduleMethod])

  // The creative choice is open to every plan, so its adoption is measured per plan. Opened is reported once the
  // list has loaded, which is when the travel costs it quotes are known.
  const ctBookingOpenReported = useRef(false)
  useEffect(() => {
    if (!choosingCreative) {
      ctBookingOpenReported.current = false
      return
    }
    if (ctBookingOpenReported.current || creativesStatus !== 'ready') return
    ctBookingOpenReported.current = true
    trackAnalytics({ type: PfRevampEventName.CT_BOOKING_OPENED, payload: ctBookingOpenedPayload(listedCreatives, isSubscribed) })
  }, [choosingCreative, creativesStatus, listedCreatives, isSubscribed, trackAnalytics])

  const pickCreative = (id: string) => {
    const creative = listedCreatives.find((entry) => entry.id === id)
    setCtBookingId(id, creative?.fullName)
    trackAnalytics({ type: PfRevampEventName.CT_BOOKING_CHOSEN, payload: ctBookingChosenPayload(id, creative, isSubscribed) })
  }

  // ── Auto-skip when nothing meaningful renders on the step ─────────────────────
  // The BE `visible` flag is the EXACT signal and short-circuits the
  // cart-shape approximation. `undefined` while the options load / on the legacy
  // path ⇒ today's approximation, so there is no new flicker window.
  const isSkipped = usePfStore(
    useMemo(
      () => selectInstructionsStepSkipped(catalogueQuery.data, instructionCatalogue.visible),
      [catalogueQuery.data, instructionCatalogue.visible]
    )
  )
  // A click on the disabled Next names the open gaps; each stays outlined until it is filled.
  const openGaps = usePfStore(
    useMemo(
      () => (state: Parameters<ReturnType<typeof selectInstructionsStepErrors>>[0]) =>
        refineInstructionsErrors(selectInstructionsStepErrors(catalogueQuery.data, instructionCatalogue.visible)(state), state).join(' '),
      [catalogueQuery.data, instructionCatalogue.visible]
    )
  )
  const revealedGaps = usePfStore((s) => s.revealedInstructionsGaps)
  const revealInstructionsGaps = usePfStore((s) => s.revealInstructionsGaps)
  useEffect(() => () => revealInstructionsGaps([]), [revealInstructionsGaps])
  const gapOutlineSx = useMemo(() => Object.fromEntries(
    revealedGaps
      .filter((gap) => openGaps.split(' ').includes(gap))
      .map((gap) => [`& [data-pf-gap~="${gap}"]`, { outline: `2px solid ${CORAL_600}`, outlineOffset: '0.4rem', borderRadius: '0.8rem' }])
  ), [revealedGaps, openGaps])

  useEffect(() => {
    if (isSkipped) {
      navigate(pfPath(PfStepPath.BILLING), { replace: true })
    }
  }, [isSkipped, navigate, pfPath])

  // ── DF-I21 — nobody to book ⇒ un-book ─────────────────────────────────────────
  // Legacy deselected the booking when the list came back empty. v2 left
  // `ctBookingId` at '' — "booking wanted, none chosen" — which FAILS the Next
  // gate, so the client was stuck on a step with nothing to pick and no hint
  // that unticking the box was the way out.
  //
  // Reads the UNFILTERED list on purpose: a name search that matches nothing is
  // not "no creative available", and clearing the booking because someone typed
  // a typo would be its own bug.
  useEffect(() => {
    if (ctBookingId === undefined) return
    if (creativesStatus !== 'ready') return
    if (listedCreatives.length > 0) return
    setCtBookingId(undefined)
    setEmptyCreativesBodyKey(creativesBodyKey)
    trackAnalytics({ type: PfRevampEventName.CT_BOOKING_UNAVAILABLE, payload: { reason: 'empty_list', trigger: 'initial' } })
  }, [ctBookingId, creativesStatus, listedCreatives.length, setCtBookingId, creativesBodyKey, trackAnalytics])

  // A failed list is the other dead end: the client asked to pick and got nothing to pick from.
  const ctBookingFailureReported = useRef(false)
  useEffect(() => {
    if (!choosingCreative || creativesStatus !== 'error') {
      ctBookingFailureReported.current = false
      return
    }
    if (ctBookingFailureReported.current) return
    ctBookingFailureReported.current = true
    trackAnalytics({ type: PfRevampEventName.CT_BOOKING_UNAVAILABLE, payload: { reason: 'request_failed', trigger: 'initial' } })
  }, [choosingCreative, creativesStatus, trackAnalytics])

  // ── §11.2 — re-derive the display-only cart-total extras line ──────────────────
  // Runs on load (from persisted `extraServices`) AND on every toggle. Value-
  // guarded by `setExtrasFromInstructions` (no dirty, no autosave churn) and
  // absent from serializeDraft/fingerprint → this is NOT a persisted-slice write.
  // DF-I24 / R-4 — offer only extras the place-order body can carry. An
  // unslotted kind would otherwise be selectable, priced, mirrored into the
  // total and then silently dropped, i.e. quoted for and never delivered. The
  // warning is the escalation path; inventing a wire field is explicitly not.
  const { unslotted: unslottedExtraKinds } = useMemo(
    () => partitionExtrasByWireSlot(instructionCatalogue.extraServices),
    [instructionCatalogue.extraServices]
  )

  // Fast delivery is a paid-plan feature: included (and not unselectable)
  // for a subscribed workspace, not offered at all otherwise. Withholding it is
  // the product rule; note it also means nobody PURCHASES it any more, so the
  // `fastDelivery` wire flag can only ever be false — see the register.
  const extrasCatalogue = useMemo(
    () => offerableExtras(instructionCatalogue.extraServices, isSubscribed),
    [instructionCatalogue.extraServices, isSubscribed]
  )
  // Legacy hid its whole "Other services" box when the catalogue advertised no
  // extra (`{extraServices.FAST_DELIVERY && …}`). Withholding fast delivery from
  // an unsubscribed workspace made that the common case, so an empty
  // shell reading "no extra services are available" would be the normal view.
  // Loading and error still render — they have something to say.
  const showExtras = instructionCatalogue.status === 'loading'
    || optionsFailed
    || Object.keys(extrasCatalogue).length > 0

  // The subscriber's card reads as checked, so the store must not ALSO carry the
  // kind: `serializePlacement` turns it into `fastDelivery: true`, and the BE
  // prices that unconditionally — the "included" card would bill 29 EUR.
  useEffect(() => {
    if (!extraServices.some((kind) => SUBSCRIPTION_ONLY_EXTRA_KINDS.has(kind))) return
    setExtraServices(extraServices.filter((kind) => !SUBSCRIPTION_ONLY_EXTRA_KINDS.has(kind)))
  }, [extraServices, setExtraServices])

  useEffect(() => {
    if (unslottedExtraKinds.length === 0) return
    logger.warn('PF v2 instructions: BE offers extras with no place-order slot — dropped from the picker (DF-I24 / R-4)', {
      context: { component: 'Instructions.page', kinds: unslottedExtraKinds.join(',') },
    })
  }, [unslottedExtraKinds])

  // Keyed on the pickup alone: the meeting object changes on every keystroke in the contact fields.
  const travelQuoteKey = JSON.stringify(keyPickupTravelQuoteRequest(meeting))
  const travelQuoteRequest = useMemo(() => JSON.parse(travelQuoteKey) as ReturnType<typeof keyPickupTravelQuoteRequest>, [travelQuoteKey])
  const travelQuote = useKeyPickupTravelCost(travelQuoteRequest)

  useEffect(() => {
    setKeyPickupTravelQuotePending(travelQuoteRequest ? !travelQuote.data : undefined)
  }, [setKeyPickupTravelQuotePending, travelQuote.data, travelQuoteRequest])

  // ── Extras toggle — writes ProductKind STRINGS into `extraServices` (§11.4) ────
  const handleExtraToggle = (kind: ProductKind, next: boolean) => {
    const nextKinds = next
      ? Array.from(new Set([...extraServices, kind as unknown as SalesforceId]))
      : extraServices.filter((k) => k !== (kind as unknown as SalesforceId))
    setExtraServices(nextKinds)

    trackAnalytics({
      type: PfRevampEventName.EXTRA_TOGGLED,
      payload: { kind, selected: next },
    })
  }

  return (
    <StepShell>
      <PfPageHeading
        title={t('purchase_flow_revamp:instructions.page_title')}
        description={t('purchase_flow_revamp:instructions.page_subtitle')}
      />

      <Stack gap={2.4} sx={gapOutlineSx}>

        {/* The step deliberately keeps working when the options call
            fails (blocking an order over a failed lookup is worse), but it used
            to do so in complete silence: a guessed three-mode form, no prices,
            no lead-time floor, and extras reporting "none available". Say so,
            and offer the retry that actually fixes it. */}
        {optionsFailed &&
          <PfRetryAlert
            data-cy="pf-revamp-instructions-options-error"
            message={t('purchase_flow_revamp:instructions.options_error')}
            onRetry={instructionCatalogue.refetch}
          />
        }

        {showScheduleSection &&
          <ShootingScheduleSection
            method={scheduleMethod}
            onPickMethod={pickScheduleMethodTracked}
            day={scheduleDay}
            onPickDay={schedule.setDay}
            hour={scheduleHour}
            minute={scheduleMinute}
            onPickHour={schedule.setHour}
            onPickMinute={schedule.setMinute}
            canChooseCreative={showCtBooking}
            choosingCreative={choosingCreative}
            onToggleChoosingCreative={toggleChoosingCreative}
            creatives={listedCreatives}
            creativeId={ctBookingId || undefined}
            onPickCreative={pickCreative}
            isCreativesLoading={creativesStatus === 'loading'}
            noCreativesAvailable={noCreativesAvailable}
            {...(minDateTime ? { minDateTime: moment(minDateTime) } : {})}
            timezone={instructionCatalogue.timezone}
            userTimezone={userTimezone}
            scheduledAt={scheduledAt}
          />
        }

        {choosingCreative && creativesStatus === 'error' &&
          <PfRetryAlert
            data-cy="pf-revamp-creatives-error"
            message={t('purchase_flow_revamp:instructions.ct_booking.error')}
            onRetry={refetchCreatives}
          />
        }

        {showMeeting && measurementDroppedMeeting &&
          <SystemMessage
            variant="info"
            message={t('purchase_flow_revamp:instructions.meeting.dropped_for_measurement')}
          />
        }

        {showMeeting && !meetingAwaitsSchedule &&
          <Box data-pf-gap="meeting_type_missing meeting_name_invalid meeting_phone_invalid meeting_email_invalid meeting_address_invalid">
          <MeetingSection
            value={meeting}
            onChange={setMeeting}
            availableModes={availableModes}
            isModesLoading={isModesLoading}
            organizationInstructions={instructionCatalogue.organizationInstructions}
            currency={instructionCatalogue.currency}
            countryCode={property?.countryCode}
            mapCenter={property?.coordinates}
            contactPrefill={contactPrefill}
            prefillTarget={prefillTarget}
            onPhoneValidityChange={setMeetingPhoneInvalid}
            isMeasurementOnly={isMeasurementOnly}
          />
          </Box>
        }

        {showMeeting && travelQuote.isError &&
          <PfRetryAlert
            data-cy="pf-revamp-key-pickup-travel-cost-error"
            message={t('purchase_flow_revamp:instructions.meeting.key_pickup_travel_cost_error')}
            onRetry={() => { travelQuote.refetch() }}
          />
        }

        {!isMeasurementOnly && showExtras &&
          <ExtrasSection
            extras={extrasCatalogue}
            currency={instructionCatalogue.currency}
            selectedKinds={extraServices}
            onToggle={handleExtraToggle}
            isLoading={instructionCatalogue.status === 'loading'}
            hasError={optionsFailed}
            subscriptionIncluded={isSubscribed}
          />
        }

        {!isMeasurementOnly &&
          <CommentsSection
            value={comments}
            onChange={setComments}
            isCreativeBriefing={showMeeting}
            isChoosingCreative={ctBookingId !== undefined}
          />
        }

      </Stack>
    </StepShell>
  )
}
